/**
 * WRMS Service — Digi Sahyadri
 * Firebase Firestore CRUD for all 4 request types + approval logs
 */

import {
  db,
} from '../../config/firebase';
import {
  collection,
  doc,
  addDoc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore';
import { getApprovalChain, normalizeApprovalChain, getDeptGroup, getNextStage, isFinalStage, isReportingManagerOfRequest, isHandoverColleagueOfRequest } from '../utils/ApprovalRouter';
import { generateRequestId } from '../utils/requestIdGenerator';
import { sendNotificationToRole, sendNotificationToUser } from './notificationService';
import { markExtraWorkInAttendance } from '../../services/firebase/attendanceService';
import { isDeptVisibleToUser } from '../../utils/departmentVisibility';
import { deductLeave, restoreLeave } from './leaveBalanceService';
import { getEmployee, getManagerName } from '../../services/firebase/employeeService';


// ── Collection Names ─────────────────────────────────────────────────────────
const COLLECTIONS = {
  leave: 'wrms_leave_requests',
  early_leave: 'wrms_early_leave_requests',
  gate_pass: 'wrms_gate_pass_requests',
  extra_work: 'wrms_extra_work_entries',
  logs: 'wrms_approval_logs',
};

// ── Submit Request ───────────────────────────────────────────────────────────

const saveAttachmentChunks = async (requestType, docId, attachmentUrl) => {
  if (!attachmentUrl) {
    console.log("saveAttachmentChunks: No attachmentUrl provided.");
    return;
  }
  const CHUNK_SIZE = 800 * 1024; // 800 KB per chunk (in characters)
  const totalLength = attachmentUrl.length;
  let chunkIndex = 0;

  console.log(`saveAttachmentChunks: Starting to save ${totalLength} characters of attachment for docId ${docId}...`);

  for (let offset = 0; offset < totalLength; offset += CHUNK_SIZE) {
    const chunk = attachmentUrl.substring(offset, offset + CHUNK_SIZE);
    const chunkDocRef = doc(db, COLLECTIONS[requestType], docId, 'attachment_chunks', chunkIndex.toString());
    console.log(`saveAttachmentChunks: Saving chunk ${chunkIndex} (size: ${chunk.length})...`);
    await setDoc(chunkDocRef, {
      chunkIndex,
      data: chunk,
    });
    chunkIndex++;
  }
  console.log(`saveAttachmentChunks: Finished saving all ${chunkIndex} chunks successfully.`);
};

/**
 * Submit a new WRMS request.
 * Auto-generates Request ID, sets current_stage, notifies first approver.
 */
export const submitRequest = async (requestType, formData, user) => {
  try {
    const cleanedFormData = { ...formData };

    // Resolve Handover Colleague details if provided
    let handoverTo = (cleanedFormData.handoverTo || '').trim();
    let handoverToId = cleanedFormData.handoverToId || '';
    let handoverToEmail = cleanedFormData.handoverToEmail || '';
    let handoverToTeamId = cleanedFormData.handoverToTeamId || '';

    if (handoverTo && !handoverToId) {
      try {
        const empSnap = await getDocs(collection(db, 'employees'));
        empSnap.forEach(d => {
          const emp = d.data();
          const fullName = emp.personal?.fullName ||
            `${emp.personal?.firstName || ''} ${emp.personal?.lastName || ''}`.trim() ||
            emp.firstName || '';
          if (fullName && fullName.toLowerCase() === handoverTo.toLowerCase()) {
            handoverToId = d.id;
            handoverToEmail = emp.personal?.email || emp.email || '';
            handoverToTeamId = emp.job?.employeeId || emp.employeeCode || emp.teamId || '';
          }
        });
      } catch (e) {
        console.warn('[wrmsService] Handover employee lookup skipped:', e);
      }
    }

    const hasHandover = Boolean(handoverTo);
    const deptGroup = getDeptGroup(user.department, user.role);
    const chain = getApprovalChain(requestType, deptGroup, { hasHandover, handoverTo });
    // First approver stage (chain[0] = submitted, chain[1] = first approver)
    const firstApproverStage = chain.length > 1 ? chain[1] : chain[0];

    const requestId = await generateRequestId(requestType);

    // Resolve reporting manager for all request types
    let reportingManagerId = user.reportingManager || user.job?.reportingManager || user.jobDetails?.reportingTo || '';
    let reportingManagerName = user.reportingManagerName || user.job?.reportingManagerName || '';

    if (!reportingManagerId) {
      try {
        const empLookupId = user.employeeId || user.id || user.uid;
        if (empLookupId) {
          const empDoc = await getEmployee(empLookupId);
          if (empDoc?.success && empDoc.data?.job) {
            reportingManagerId = empDoc.data.job.reportingManager || '';
            reportingManagerName = empDoc.data.job.reportingManagerName || '';
          }
        }
      } catch (err) {
        console.warn('[wrmsService] Could not lookup reporting manager from employee:', err);
      }
    }

    if (reportingManagerId && !reportingManagerName) {
      try {
        reportingManagerName = await getManagerName(reportingManagerId);
      } catch (err) {
        console.warn('[wrmsService] Could not resolve manager name:', err);
      }
    }

    // Extract attachmentUrl if it exists
    let attachmentUrl = '';
    let isAttachmentChunked = false;

    console.log("submitRequest input formData:", formData);

    const CHUNK_THRESHOLD = 800 * 1024; // 800 KB threshold

    if (requestType === 'extra_work' && cleanedFormData.attachmentUrl) {
      if (cleanedFormData.attachmentUrl.length > CHUNK_THRESHOLD) {
        attachmentUrl = cleanedFormData.attachmentUrl;
        cleanedFormData.attachmentUrl = '[chunked]';
        isAttachmentChunked = true;
        console.log("submitRequest: Attachment is large. Chunking in subcollection. Length:", attachmentUrl.length);
      } else {
        console.log("submitRequest: Attachment is small. Storing directly in document. Length:", cleanedFormData.attachmentUrl.length);
      }
    }

    const docData = {
      requestId,
      requestType,
      employeeId: user.uid || user.id,
      employeeName: user.employeeName || user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
      teamId: user.employeeCode || user.teamId || '',
      designation: user.designation || '',
      department: user.department || '',
      reportingManagerId: reportingManagerId || '',
      reportingManagerName: reportingManagerName || '',
      handoverTo: handoverTo || '',
      handoverToId: handoverToId || '',
      handoverToEmail: handoverToEmail || '',
      handoverToTeamId: handoverToTeamId || '',
      deptGroup,
      approvalChain: chain,
      currentStage: firstApproverStage.stage,
      status: 'pending',
      submittedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      applicationApprovedOn: null,
      ...cleanedFormData,
      isAttachmentChunked,
    };

    console.log("submitRequest docData to write to Firestore:", docData);

    const colRef = collection(db, COLLECTIONS[requestType]);
    const docRef = await addDoc(colRef, docData);

    console.log("submitRequest: Document written successfully with ID:", docRef.id);

    await addApprovalLog({
      requestId: docRef.id,
      requestType,
      approverId: user.uid || user.id,
      approverRole: 'employee',
      approverName: docData.employeeName,
      action: 'submitted',
      stage: 'submitted',
      remarks: 'Request submitted by employee',
    });

    // Notify first approver
    if (firstApproverStage.stage === 'handover') {
      if (handoverToId) {
        try {
          await sendNotificationToUser(handoverToId, {
            type: 'approval_required',
            title: `Handover Request from ${docData.employeeName}`,
            message: `${docData.employeeName} has assigned their work responsibilities to you for their ${getRequestTypeLabel(requestType)} request. Please review and approve.`,
            requestId: docRef.id,
            requestType,
            requestDocId: requestId,
          });
        } catch (notifErr) {
          console.warn('Failed to notify handover colleague by ID:', notifErr);
        }
      }
    } else if (firstApproverStage.stage === 'manager') {
      if (reportingManagerId) {
        try {
          await sendNotificationToUser(reportingManagerId, {
            type: 'approval_required',
            title: `New ${getRequestTypeLabel(requestType)} Request`,
            message: `${docData.employeeName} has submitted a ${getRequestTypeLabel(requestType)} request awaiting your approval.`,
            requestId: docRef.id,
            requestType,
            requestDocId: requestId,
          });
        } catch (notifErr) {
          console.warn('Failed to notify manager by ID:', notifErr);
        }
      } else {
        // Fallback: If employee has no designated reporting manager assigned, route to HR so request is not stranded
        try {
          await sendNotificationToRole('hr', {
            type: 'approval_required',
            title: `New ${getRequestTypeLabel(requestType)} Request`,
            message: `${docData.employeeName} has submitted a ${getRequestTypeLabel(requestType)} request awaiting approval.`,
            requestId: docRef.id,
            requestType,
            requestDocId: requestId,
          }, user.department);
        } catch (err) {
          console.warn('Failed to notify HR fallback:', err);
        }
      }
    } else if (firstApproverStage.role && firstApproverStage.role !== 'handover_employee' && firstApproverStage.role !== 'manager') {
      await sendNotificationToRole(firstApproverStage.role, {
        type: 'approval_required',
        title: `New ${getRequestTypeLabel(requestType)} Request`,
        message: `${docData.employeeName} has submitted a ${getRequestTypeLabel(requestType)} request. Please review.`,
        requestId: docRef.id,
        requestType,
        requestDocId: requestId,
      }, user.department);
    }

    return { success: true, id: docRef.id, requestId };
  } catch (error) {
    console.error('[wrmsService] submitRequest error:', error);
    throw error;
  }
};

// ── Approve Stage ────────────────────────────────────────────────────────────

export const approveStage = async (requestType, docId, approverUser, remarks = '') => {
  try {
    const colRef = collection(db, COLLECTIONS[requestType]);
    const docRef = doc(db, COLLECTIONS[requestType], docId);
    const snap = await getDoc(docRef);
    if (!snap.exists()) throw new Error('Request not found');

    const data = snap.data();
    const { currentStage, deptGroup, employeeId, employeeName, requestId, status } = data;

    const isReapprovingFromHold = status === 'on_hold';
    const nextStep = getNextStage(requestType, deptGroup, currentStage, data);
    const isLast = isFinalStage(requestType, deptGroup, currentStage, data) || isReapprovingFromHold;

    const updatePayload = {
      updatedAt: serverTimestamp(),
    };

    if (isLast) {
      // Final approval
      updatePayload.status = 'approved';
      updatePayload.currentStage = 'approved';
      updatePayload.applicationApprovedOn = serverTimestamp();

      // If it's extra work, add it to the attendance system
      if (requestType === 'extra_work') {
        try {
          await markExtraWorkInAttendance(
            data.teamId || employeeId,
            employeeName,
            data.department,
            data.workDate,
            data.startTime,
            data.endTime,
            data.totalHours
          );
        } catch (err) {
          console.error("Failed to mark extra work in attendance", err);
        }
      }

      // If it's leave, deduct days from leave balance
      if (requestType === 'leave') {
        try {
          if (data.leaveType && data.daysCount) {
            await deductLeave(data.employeeId, data.leaveType, Number(data.daysCount));
            console.log(`[wrmsService] Deducted ${data.daysCount} days of ${data.leaveType} for ${data.employeeName}`);
          }
        } catch (leaveErr) {
          console.error("Failed to deduct leave balance on approval:", leaveErr);
        }
      }

      // Notify employee — final approved
      await sendNotificationToUser(employeeId, {
        type: 'request_approved',
        title: 'Request Approved',
        message: `Your ${getRequestTypeLabel(requestType)} request (${requestId}) has been finally approved.`,
        requestId: docId,
        requestType,
        requestDocId: requestId,
      });
    } else {
      // Advance to next stage
      updatePayload.currentStage = nextStep.stage;

      const actorName = currentStage === 'handover'
        ? (data.handoverTo ? `handover colleague (${data.handoverTo})` : 'handover colleague')
        : currentStage === 'manager'
        ? 'Reporting Manager'
        : approverUser.role?.replace(/_/g, ' ') || 'Approver';

      // Notify employee of stage update
      await sendNotificationToUser(employeeId, {
        type: 'stage_update',
        title: `Request Progressing`,
        message: `Your ${getRequestTypeLabel(requestType)} request (${requestId}) has been approved by ${actorName}. Next: ${nextStep.label}.`,
        requestId: docId,
        requestType,
        requestDocId: requestId,
      });

      // If next stage is Reporting Manager, notify manager directly by ID
      if (nextStep.stage === 'manager' && data.reportingManagerId) {
        try {
          await sendNotificationToUser(data.reportingManagerId, {
            type: 'approval_required',
            title: `New ${getRequestTypeLabel(requestType)} Request`,
            message: `${employeeName}'s ${getRequestTypeLabel(requestType)} request (${requestId}) was approved by handover colleague and is now awaiting your approval.`,
            requestId: docId,
            requestType,
            requestDocId: requestId,
          });
        } catch (notifErr) {
          console.warn('Failed to notify manager by ID:', notifErr);
        }
      }

      // Notify next approver role
      if (nextStep.role && nextStep.role !== 'manager') {
        await sendNotificationToRole(nextStep.role, {
          type: 'approval_required',
          title: `${getRequestTypeLabel(requestType)} Awaiting Your Approval`,
          message: `${employeeName}'s request (${requestId}) requires your approval.`,
          requestId: docId,
          requestType,
          requestDocId: requestId,
        }, data.department);
      }
    }

    await updateDoc(docRef, updatePayload);

    await addApprovalLog({
      requestId: docId,
      requestType,
      approverId: approverUser.uid || approverUser.id,
      approverRole: currentStage === 'handover' ? 'handover_employee' : (approverUser.role || currentStage),
      approverName: approverUser.employeeName || approverUser.displayName || approverUser.personal?.fullName || approverUser.email,
      action: isReapprovingFromHold ? 're_approved' : (currentStage === 'handover' ? 'handover_accepted' : 'approved'),
      stage: currentStage,
      remarks: remarks || (isReapprovingFromHold ? 'Approved from Hold' : (currentStage === 'handover' ? 'Handover responsibility accepted' : `Approved at stage: ${getStageName(currentStage)}`)),
    });

    return { success: true };
  } catch (error) {
    console.error('[wrmsService] approveStage error:', error);
    throw error;
  }
};

// ── Put Request On Hold (HR, CEO, Founder, Admin) ─────────────────────────────

export const putOnHoldRequest = async (requestType, docId, approverUser, remarks) => {
  try {
    const docRef = doc(db, COLLECTIONS[requestType], docId);
    const snap = await getDoc(docRef);
    if (!snap.exists()) throw new Error('Request not found');

    const data = snap.data();

    // Guard: Approved request can only be put on hold on or before its From Date
    if (data.status === 'approved' && !isApprovedRequestActionableByFromDate(data)) {
      throw new Error('Action not allowed: An approved request can only be put on hold on or before its From Date.');
    }

    // If leave was previously approved, restore the deducted leave days while on hold!
    if (data.status === 'approved' && requestType === 'leave') {
      try {
        if (data.leaveType && data.daysCount) {
          await restoreLeave(data.employeeId, data.leaveType, Number(data.daysCount));
          console.log(`[wrmsService] Restored ${data.daysCount} days of ${data.leaveType} for ${data.employeeName} (put on hold)`);
        }
      } catch (err) {
        console.error("Failed to restore leave balance on hold:", err);
      }
    }

    await updateDoc(docRef, {
      status: 'on_hold',
      currentStage: 'on_hold',
      holdReason: remarks,
      heldBy: approverUser.employeeName || approverUser.displayName || approverUser.email,
      heldByRole: approverUser.role,
      updatedAt: serverTimestamp(),
    });

    // Notify employee
    await sendNotificationToUser(data.employeeId, {
      type: 'request_held',
      title: 'Leave Request Put On Hold',
      message: `Your ${getRequestTypeLabel(requestType)} request (${data.requestId}) has been put on hold by ${approverUser.role?.toUpperCase()}. Reason: ${remarks}`,
      requestId: docId,
      requestType,
      requestDocId: data.requestId,
    });

    await addApprovalLog({
      requestId: docId,
      requestType,
      approverId: approverUser.uid || approverUser.id,
      approverRole: approverUser.role,
      approverName: approverUser.employeeName || approverUser.displayName || approverUser.email,
      action: 'on_hold',
      stage: data.currentStage || 'on_hold',
      remarks: remarks || `Put on hold by ${approverUser.role}`,
    });

    return { success: true };
  } catch (error) {
    console.error('[wrmsService] putOnHoldRequest error:', error);
    throw error;
  }
};

// ── Reject Request ───────────────────────────────────────────────────────────

export const rejectRequest = async (requestType, docId, approverUser, remarks) => {
  try {
    const docRef = doc(db, COLLECTIONS[requestType], docId);
    const snap = await getDoc(docRef);
    if (!snap.exists()) throw new Error('Request not found');

    const data = snap.data();

    // Guard: Approved request can only be rejected on or before its From Date
    if (data.status === 'approved' && !isApprovedRequestActionableByFromDate(data)) {
      throw new Error('Action not allowed: An approved request can only be rejected on or before its From Date.');
    }

    // If leave was previously approved, restore the deducted leave days!
    if (data.status === 'approved' && requestType === 'leave') {
      try {
        if (data.leaveType && data.daysCount) {
          await restoreLeave(data.employeeId, data.leaveType, Number(data.daysCount));
          console.log(`[wrmsService] Restored ${data.daysCount} days of ${data.leaveType} for ${data.employeeName} (rejected after approval)`);
        }
      } catch (err) {
        console.error("Failed to restore leave balance on reject:", err);
      }
    }

    await updateDoc(docRef, {
      status: 'rejected',
      currentStage: 'rejected',
      rejectionReason: remarks,
      rejectedBy: approverUser.employeeName || approverUser.displayName || approverUser.email,
      rejectedByRole: approverUser.role,
      updatedAt: serverTimestamp(),
    });

    // Notify employee
    await sendNotificationToUser(data.employeeId, {
      type: 'request_rejected',
      title: 'Request Rejected',
      message: `Your ${getRequestTypeLabel(requestType)} request (${data.requestId}) was rejected by ${approverUser.role?.toUpperCase()}. Reason: ${remarks}`,
      requestId: docId,
      requestType,
      requestDocId: data.requestId,
    });

    await addApprovalLog({
      requestId: docId,
      requestType,
      approverId: approverUser.uid || approverUser.id,
      approverRole: approverUser.role,
      approverName: approverUser.employeeName || approverUser.displayName || approverUser.email,
      action: 'rejected',
      stage: data.currentStage,
      remarks,
    });

    return { success: true };
  } catch (error) {
    console.error('[wrmsService] rejectRequest error:', error);
    throw error;
  }
};

// ── HR/Admin Override ────────────────────────────────────────────────────────

export const overrideRequest = async (requestType, docId, newStatus, adminUser, reason) => {
  try {
    const docRef = doc(db, COLLECTIONS[requestType], docId);
    const snap = await getDoc(docRef);
    if (!snap.exists()) throw new Error('Request not found');
    const data = snap.data();

    const updatePayload = {
      status: newStatus,
      currentStage: newStatus,
      updatedAt: serverTimestamp(),
    };

    if (newStatus === 'approved') {
      updatePayload.applicationApprovedOn = serverTimestamp();

      if (requestType === 'extra_work') {
        try {
          await markExtraWorkInAttendance(
            data.teamId || data.employeeId,
            data.employeeName,
            data.department,
            data.workDate,
            data.startTime,
            data.endTime,
            data.totalHours
          );
        } catch (err) {
          console.error("Failed to mark extra work in attendance (override)", err);
        }
      }

      if (requestType === 'leave' && data.status !== 'approved') {
        try {
          if (data.leaveType && data.daysCount) {
            await deductLeave(data.employeeId, data.leaveType, Number(data.daysCount));
          }
        } catch (err) {
          console.error("Failed to deduct leave in override:", err);
        }
      }
    } else if (newStatus === 'rejected' || newStatus === 'on_hold') {
      if (data.status === 'approved' && !isApprovedRequestActionableByFromDate(data)) {
        throw new Error(`Action not allowed: An approved request can only be set to ${newStatus} on or before its From Date.`);
      }

      if (newStatus === 'rejected') updatePayload.rejectionReason = reason;
      if (newStatus === 'on_hold') updatePayload.holdReason = reason;

      if (data.status === 'approved' && requestType === 'leave') {
        try {
          if (data.leaveType && data.daysCount) {
            await restoreLeave(data.employeeId, data.leaveType, Number(data.daysCount));
          }
        } catch (err) {
          console.error("Failed to restore leave in override:", err);
        }
      }
    }

    await updateDoc(docRef, updatePayload);

    await addApprovalLog({
      requestId: docId,
      requestType,
      approverId: adminUser.uid || adminUser.id,
      approverRole: adminUser.role,
      approverName: adminUser.employeeName || adminUser.displayName || 'Admin',
      action: 'overridden',
      stage: data.currentStage,
      remarks: `Admin override to ${newStatus}. Reason: ${reason}`,
    });

    // Notify employee
    await sendNotificationToUser(data.employeeId, {
      type: newStatus === 'approved' ? 'request_approved' : 'request_rejected',
      title: `Request ${newStatus === 'approved' ? 'Approved' : newStatus === 'on_hold' ? 'Put On Hold' : 'Rejected'} by Admin`,
      message: `Your ${getRequestTypeLabel(requestType)} request (${data.requestId}) has been ${newStatus} by admin. Reason: ${reason}`,
      requestId: docId,
      requestType,
      requestDocId: data.requestId,
    });

    return { success: true };
  } catch (error) {
    console.error('[wrmsService] overrideRequest error:', error);
    throw error;
  }
};

// ── Cancel Request (Employee Only) ──────────────────────────────────────────

/**
 * Resolves the start date ("From Date") of any request.
 * For Leave: fromDate
 * For Early Leave: fromDate (or date)
 * For Gate Pass: date (or returnDate)
 * For Extra Work: workDate (or date)
 */
export const getRequestStartDate = (request) => {
  if (!request) return null;
  const rawDate = request.fromDate || request.date || request.workDate || request.toDate;
  if (!rawDate) return null;

  let d;
  if (rawDate.toDate) {
    d = rawDate.toDate();
  } else if (typeof rawDate === 'string' && rawDate.includes('-')) {
    const parts = rawDate.split('T')[0].split('-');
    if (parts.length === 3) {
      d = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
    } else {
      d = new Date(rawDate);
    }
  } else {
    d = new Date(rawDate);
  }

  if (isNaN(d.getTime())) return null;

  // Normalized to the start of the day (00:00:00.000)
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * Checks if an approved request can still be rejected or put on hold by management.
 * Action is allowed ONLY PRIOR TO the start date ("From Date") of the request.
 * Once the From Date arrives or has passed (today >= From Date), the leave has begun/ended,
 * so Reject and Hold actions are locked and hidden.
 */
export const isApprovedRequestActionableByFromDate = (request) => {
  if (!request) return false;
  const startDate = getRequestStartDate(request);
  if (!startDate) return true; // If no date found, allow fallback

  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);

  // Must be strictly before the start date (today < startDate)
  return todayStart.getTime() < startDate.getTime();
};

/**
 * Helper to determine the last active date of a request.
 * For Leave: toDate (or fromDate)
 * For Early Leave: fromDate (or date)
 * For Gate Pass: returnDate (or date)
 * For Extra Work: workDate
 */
export const getRequestEndDate = (request) => {
  if (!request) return null;
  const rawDate = request.toDate || request.returnDate || request.workDate || request.fromDate || request.date;
  if (!rawDate) return null;

  let d;
  if (rawDate.toDate) {
    d = rawDate.toDate();
  } else if (typeof rawDate === 'string' && rawDate.includes('-')) {
    const parts = rawDate.split('T')[0].split('-');
    if (parts.length === 3) {
      d = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
    } else {
      d = new Date(rawDate);
    }
  } else if (typeof rawDate === 'string' && rawDate.includes('/')) {
    const parts = rawDate.split('/');
    if (parts.length === 3) {
      if (parts[0].length === 4) {
        d = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
      } else {
        d = new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
      }
    } else {
      d = new Date(rawDate);
    }
  } else {
    d = new Date(rawDate);
  }

  if (isNaN(d.getTime())) return null;

  // Set to the end of that day (23:59:59.999)
  d.setHours(23, 59, 59, 999);
  return d;
};

/**
 * Checks if a request is eligible for cancellation based on date.
 * A request can ONLY be cancelled on or before the leave / request date.
 */
export const isRequestCancellableByDate = (request) => {
  const endDate = getRequestEndDate(request);
  if (!endDate) return true; // If no date found, allow
  return Date.now() <= endDate.getTime();
};

/**
 * Checks if a Gate Pass's Time In can still be edited based on date.
 * Strictly allowed only on or before the gate pass date (up to the end of that day).
 */
export const isGatePassTimeInEditableByDate = (request) => {
  if (!request) return false;
  const endDate = getRequestEndDate(request);
  if (!endDate) return true;
  return Date.now() <= endDate.getTime();
};

export const cancelRequest = async (requestType, docId, user, reason = '') => {
  try {
    const docRef = doc(db, COLLECTIONS[requestType], docId);
    const snap = await getDoc(docRef);
    if (!snap.exists()) throw new Error('Request not found');
    const data = snap.data();

    // Strict access control: ONLY the employee who submitted the request can cancel it
    const currentUserId = user?.uid || user?.id;
    if (!currentUserId || data.employeeId !== currentUserId) {
      throw new Error('Access denied: Only the employee who applied for this request can cancel it.');
    }

    if (data.status === 'cancelled') {
      throw new Error('This request is already cancelled.');
    }

    if (data.status === 'rejected') {
      throw new Error('Rejected requests cannot be cancelled.');
    }

    // Date restriction: Cancellation is only allowed up to the leave/request date
    if (!isRequestCancellableByDate(data)) {
      throw new Error('Cancellation expired: A request can only be cancelled on or before the leave date. You cannot cancel past leaves.');
    }

    // If an approved leave is cancelled, refund/restore the leave balance
    if (data.status === 'approved' && requestType === 'leave') {
      try {
        if (data.leaveType && data.daysCount) {
          await restoreLeave(data.employeeId, data.leaveType, data.daysCount);
          console.log(`[wrmsService] Restored ${data.daysCount} days of ${data.leaveType} for ${data.employeeName}`);
        }
      } catch (refundErr) {
        console.error('[wrmsService] Failed to restore leave balance on cancellation:', refundErr);
      }
    }

    // Update document to cancelled status
    await updateDoc(docRef, {
      status: 'cancelled',
      currentStage: 'cancelled',
      cancellationReason: reason || 'Cancelled by employee',
      cancelledAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    // Add immutable audit log
    await addApprovalLog({
      requestId: docId,
      requestType,
      approverId: currentUserId,
      approverRole: 'employee',
      approverName: data.employeeName || user.employeeName || user.displayName || 'Employee',
      action: 'cancelled',
      stage: 'cancelled',
      remarks: reason ? `Cancelled by employee: ${reason}` : 'Cancelled by employee',
    });

    // Send notification to HR
    try {
      await sendNotificationToRole('hr', {
        type: 'request_cancelled',
        title: 'Request Cancelled',
        message: `${data.employeeName} cancelled their ${getRequestTypeLabel(requestType)} (${data.requestId || docId}). Reason: ${reason || 'Not specified'}`,
        requestId: docId,
        requestType,
        requestDocId: data.requestId,
      }, data.department);
    } catch (notifErr) {
      console.error('[wrmsService] Failed to send cancellation notification:', notifErr);
    }

    return { success: true };
  } catch (error) {
    console.error('[wrmsService] cancelRequest error:', error);
    throw error;
  }
};

/**
 * Update ONLY the Time In (Expected Return Time) for a Gate Pass request.
 * Strictly permitted on or before the gate pass date (present date).
 * All other fields of the gate pass remain completely unchanged and locked.
 */
export const updateGatePassTimeIn = async (docId, newTimeIn, user) => {
  try {
    if (!newTimeIn || typeof newTimeIn !== 'string' || !newTimeIn.trim()) {
      throw new Error('Please enter a valid Time In.');
    }

    const docRef = doc(db, COLLECTIONS.gate_pass, docId);
    const snap = await getDoc(docRef);
    if (!snap.exists()) throw new Error('Gate pass request not found.');
    const data = snap.data();

    // Access control: only the applicant employee or elevated roles (HR/Admin) can update
    const currentUserId = user?.uid || user?.id;
    const isOwner = Boolean(currentUserId && data.employeeId === currentUserId);
    const userRoleLower = (user?.role || '').toLowerCase();
    const isElevatedRole = ['admin', 'hr', 'ceo', 'founder'].includes(userRoleLower);
    if (!isOwner && !isElevatedRole) {
      throw new Error('Access denied: Only the employee who applied (or HR/Admin) can update Time In.');
    }

    if (data.status === 'cancelled') {
      throw new Error('Cannot update Time In for a cancelled gate pass.');
    }
    if (data.status === 'rejected') {
      throw new Error('Cannot update Time In for a rejected gate pass.');
    }

    if (!isGatePassTimeInEditableByDate(data)) {
      throw new Error('Time In can only be edited on or before the gate pass date. The edit window for this date has expired.');
    }

    const trimmedTime = newTimeIn.trim();
    const editorName = user?.employeeName || user?.displayName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.email || 'Employee';
    const previousTime = data.expectedTimeIn || data.timeIn || '—';

    // Update ONLY expectedTimeIn and timeIn with edit tracking metadata
    await updateDoc(docRef, {
      expectedTimeIn: trimmedTime,
      timeIn: trimmedTime,
      isTimeInEdited: true,
      previousTimeIn: previousTime,
      timeInUpdatedAt: serverTimestamp(),
      timeInUpdatedBy: currentUserId,
      timeInUpdatedByName: editorName,
      timeInUpdatedByRole: isOwner ? 'employee' : (user?.role || 'admin'),
      updatedAt: serverTimestamp(),
    });

    // Add immutable audit trail log
    await addApprovalLog({
      requestId: docId,
      requestType: 'gate_pass',
      approverId: currentUserId,
      approverRole: isOwner ? 'employee' : (user?.role || 'admin'),
      approverName: editorName,
      action: 'time_in_updated',
      stage: data.currentStage || 'gate_pass',
      remarks: `Time In / Return Time edited from ${previousTime} to ${trimmedTime} by ${editorName}`,
    });

    return { success: true, newTimeIn: trimmedTime, editorName };
  } catch (error) {
    console.error('[wrmsService] updateGatePassTimeIn error:', error);
    throw error;
  }
};

// ── Fetch Functions ──────────────────────────────────────────────────────────

/**
 * Get all requests for a specific employee (by employeeId)
 */
export const getRequestsByEmployee = async (employeeId, requestType = null) => {
  try {
    const types = requestType ? [requestType] : Object.keys(COLLECTIONS).filter(k => k !== 'logs');
    const results = [];

    for (const type of types) {
      const q = query(
        collection(db, COLLECTIONS[type]),
        where('employeeId', '==', employeeId)
      );
      const snap = await getDocs(q);
      snap.forEach(d => results.push({ id: d.id, requestType: type, ...d.data() }));
    }

    return results.sort((a, b) => {
      const ta = a.submittedAt?.toMillis?.() || 0;
      const tb = b.submittedAt?.toMillis?.() || 0;
      return tb - ta;
    });
  } catch (error) {
    console.error('[wrmsService] getRequestsByEmployee error:', error);
    throw error;
  }
};

/**
 * Get pending requests awaiting Handover acceptance/approval by this user.
 */
export const getRequestsForHandover = async (user) => {
  if (!user) return [];
  try {
    const types = ['leave', 'early_leave', 'gate_pass'];
    const results = [];

    for (const type of types) {
      const q = query(
        collection(db, COLLECTIONS[type]),
        where('currentStage', '==', 'handover'),
        where('status', '==', 'pending')
      );
      const snap = await getDocs(q);
      snap.forEach(d => {
        const data = { id: d.id, requestType: type, ...d.data() };
        if (isHandoverColleagueOfRequest(user, data)) {
          results.push(data);
        }
      });
    }

    return results.sort((a, b) => {
      const ta = a.submittedAt?.toMillis?.() || 0;
      const tb = b.submittedAt?.toMillis?.() || 0;
      return ta - tb;
    });
  } catch (error) {
    console.error('[wrmsService] getRequestsForHandover error:', error);
    return [];
  }
};

/**
 * Get requests pending for a specific approver role
 */
export const getRequestsForApprover = async (approverRole, department = null, user = null) => {
  try {
    const types = Object.keys(COLLECTIONS).filter(k => k !== 'logs');
    const results = [];
    const approverUser = user || { role: approverRole, department };
    const approverId = approverUser.uid || approverUser.id;
    const roleLower = (approverUser.role || approverRole || '').toLowerCase().trim();
    const isElevatedRole = ['admin', 'hr', 'ceo', 'founder'].includes(roleLower);

    // Fetch inactive employee IDs to filter them out of approver queue
    const inactiveEmpIds = new Set();
    try {
      const empSnap = await getDocs(collection(db, 'employees'));
      empSnap.forEach(doc => {
        const d = doc.data();
        if (d.isDeleted === true || (d.status || '').toLowerCase() === 'inactive' || d.isActive === false) {
          inactiveEmpIds.add(doc.id);
          if (d.employeeId) inactiveEmpIds.add(String(d.employeeId));
          if (d.job?.employeeCode) inactiveEmpIds.add(String(d.job.employeeCode));
          if (d.employeeCode) inactiveEmpIds.add(String(d.employeeCode));
          if (d.uid) inactiveEmpIds.add(String(d.uid));
        }
      });
    } catch (e) {
      console.warn('[wrmsService] Inactive check skipped:', e);
    }

    const seenIds = new Set();

    for (const type of types) {
      // 1. Check requests at 'manager' stage (and legacy 'dept_head'/'production_head' stages)
      // These MUST ONLY be shown to the applicant's designated Reporting Manager (or elevated admin/hr)
      const managerStages = ['manager', 'dept_head', 'production_head'];
      for (const stg of managerStages) {
        const mgrQ = query(
          collection(db, COLLECTIONS[type]),
          where('currentStage', '==', stg),
          where('status', '==', 'pending')
        );
        const mgrSnap = await getDocs(mgrQ);
        mgrSnap.forEach(d => {
          if (seenIds.has(d.id)) return;
          const data = { id: d.id, requestType: type, ...d.data() };
          if (data.employeeId && inactiveEmpIds.has(String(data.employeeId))) return;
          if (approverId && data.employeeId === approverId) return;

          // Elevated roles (admin, hr, ceo, founder) have company-wide visibility
          if (isElevatedRole) {
            seenIds.add(d.id);
            results.push(data);
            return;
          }

          // Strict Reporting Manager check: ONLY the applicant's designated Reporting Manager can view/approve!
          // Production Head or other heads will NOT receive this unless they are the direct reporting manager.
          if (isReportingManagerOfRequest(approverUser, data)) {
            seenIds.add(d.id);
            results.push(data);
          }
        });
      }

      // 2. Check requests at 'handover' stage
      // MUST ONLY be shown to the applicant's designated Handover Colleague (or elevated admin/hr)
      const handoverQ = query(
        collection(db, COLLECTIONS[type]),
        where('currentStage', '==', 'handover'),
        where('status', '==', 'pending')
      );
      const handoverSnap = await getDocs(handoverQ);
      handoverSnap.forEach(d => {
        if (seenIds.has(d.id)) return;
        const data = { id: d.id, requestType: type, ...d.data() };
        if (data.employeeId && inactiveEmpIds.has(String(data.employeeId))) return;
        if (approverId && data.employeeId === approverId) return;

        if (isElevatedRole || isHandoverColleagueOfRequest(approverUser, data)) {
          seenIds.add(d.id);
          results.push(data);
        }
      });

      // 3. Check requests at 'hr' stage
      // Shown to HR and Admin
      if (isElevatedRole || roleLower === 'hr' || roleLower === 'admin') {
        const hrQ = query(
          collection(db, COLLECTIONS[type]),
          where('currentStage', '==', 'hr'),
          where('status', '==', 'pending')
        );
        const hrSnap = await getDocs(hrQ);
        hrSnap.forEach(d => {
          if (seenIds.has(d.id)) return;
          const data = { id: d.id, requestType: type, ...d.data() };
          if (data.employeeId && inactiveEmpIds.has(String(data.employeeId))) return;
          if (approverId && data.employeeId === approverId) return;

          seenIds.add(d.id);
          results.push(data);
        });
      }
    }

    return results.sort((a, b) => {
      const ta = a.submittedAt?.toMillis?.() || 0;
      const tb = b.submittedAt?.toMillis?.() || 0;
      return ta - tb; // oldest first for approver inbox
    });
  } catch (error) {
    console.error('[wrmsService] getRequestsForApprover error:', error);
    throw error;
  }
};

/**
 * Get all requests (HR/Admin) with optional filters
 */
export const getAllRequests = async (filters = {}, user = null) => {
  try {
    const { requestType, status, department, employeeId, startDate, endDate } = filters;
    const types = requestType ? [requestType] : Object.keys(COLLECTIONS).filter(k => k !== 'logs');
    const results = [];

    for (const type of types) {
      const constraints = [];
      if (status) constraints.push(where('status', '==', status));

      const q = query(collection(db, COLLECTIONS[type]), ...constraints);
      const snap = await getDocs(q);
      snap.forEach(d => {
        const data = { id: d.id, requestType: type, ...d.data() };
        
        // Scope restriction for dept_head / production_head if user is supplied
        if (user && (user.role === 'dept_head' || user.role === 'production_head')) {
          if (!isDeptVisibleToUser(data.department, data.department, user)) return;
        }

        // Client-side filters
        if (department && !isDeptVisibleToUser(data.department, data.department, { role: 'dept_head', department })) return;
        if (employeeId && data.employeeId !== employeeId) return;
        results.push(data);
      });
    }

    return results.sort((a, b) => {
      const ta = a.submittedAt?.toMillis?.() || 0;
      const tb = b.submittedAt?.toMillis?.() || 0;
      return tb - ta;
    });
  } catch (error) {
    console.error('[wrmsService] getAllRequests error:', error);
    throw error;
  }
};

/**
 * Get a single request by ID and type
 */
export const getRequestById = async (requestType, docId) => {
  try {
    const docRef = doc(db, COLLECTIONS[requestType], docId);
    const snap = await getDoc(docRef);
    if (!snap.exists()) return null;
    const data = snap.data();

    console.log("getRequestById fetched docData:", data);

    if (requestType === 'extra_work' && data.isAttachmentChunked) {
      console.log("getRequestById: Attachment is chunked. Fetching chunks...");
      try {
        const chunksColRef = collection(db, COLLECTIONS[requestType], docId, 'attachment_chunks');
        const chunksSnap = await getDocs(chunksColRef);

        console.log(`getRequestById: Found ${chunksSnap.size} chunks in subcollection.`);

        const chunks = [];
        chunksSnap.forEach(chunkDoc => {
          chunks.push(chunkDoc.data());
        });

        chunks.sort((a, b) => a.chunkIndex - b.chunkIndex);

        let fullAttachmentUrl = '';
        chunks.forEach(chunk => {
          fullAttachmentUrl += chunk.data || '';
        });

        data.attachmentUrl = fullAttachmentUrl;
        console.log("getRequestById: Successfully reconstructed attachment. Length:", fullAttachmentUrl.length);
      } catch (err) {
        console.error('[wrmsService] Failed to load attachment chunks:', err);
      }
    } else if (requestType === 'extra_work' && data.attachmentUrl) {
      console.log("getRequestById: Attachment is stored directly in document. Length:", data.attachmentUrl.length);
    }

    // Normalize approvalChain to guarantee no legacy production_head or dept_head steps exist
    data.approvalChain = normalizeApprovalChain(data);

    // If currentStage is legacy dept_head or production_head, map to manager
    if (data.currentStage === 'dept_head' || data.currentStage === 'production_head') {
      data.currentStage = 'manager';
    }

    // Resolve reporting manager details if missing from document
    if (!data.reportingManagerId && data.employeeId) {
      try {
        const empDoc = await getEmployee(data.employeeId);
        if (empDoc?.success && empDoc.data?.job) {
          data.reportingManagerId = empDoc.data.job.reportingManager || '';
          data.reportingManagerName = empDoc.data.job.reportingManagerName || '';
          if (data.reportingManagerId && !data.reportingManagerName) {
            data.reportingManagerName = await getManagerName(data.reportingManagerId);
          }
        }
      } catch (err) {
        console.warn('[wrmsService] getRequestById manager lookup fallback error:', err);
      }
    }

    return { id: snap.id, requestType, ...data };
  } catch (error) {
    console.error('[wrmsService] getRequestById error:', error);
    throw error;
  }
};

/**
 * Get approval logs for a request (immutable audit trail)
 */
export const getApprovalLogs = async (requestDocId) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.logs),
      where('requestId', '==', requestDocId)
    );
    const snap = await getDocs(q);
    const logs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    return logs.sort((a, b) => {
      const ta = a.actedAt?.toMillis?.() || 0;
      const tb = b.actedAt?.toMillis?.() || 0;
      return ta - tb;
    });
  } catch (error) {
    console.error('[wrmsService] getApprovalLogs error:', error);
    return [];
  }
};

/**
 * Get all requests that this approver has either approved, rejected, or is in the approval chain for.
 */
export const getRequestsHistoryForApprover = async (user) => {
  try {
    const role = user?.role || 'employee';

    const types = Object.keys(COLLECTIONS).filter(k => k !== 'logs');
    const results = [];

    for (const type of types) {
      const q = query(collection(db, COLLECTIONS[type]));
      const snap = await getDocs(q);
      snap.forEach(d => {
        const data = { id: d.id, requestType: type, ...d.data() };

        const isMgr = isReportingManagerOfRequest(user, data);
        const isHandover = isHandoverColleagueOfRequest(user, data);

        if (isMgr || isHandover) {
          results.push(data);
          return;
        }

        // Dept Head and Production Head must ONLY see history from their authorized departments
        if (role === 'dept_head' || role === 'production_head') {
          if (!isDeptVisibleToUser(data.department, data.department, user)) {
            return;
          }
        } else {
          // Check if user role is in chain or same department
          const inChain = data.approvalChain?.some(step => step.role === role);
          const sameDept = isDeptVisibleToUser(data.department, data.department, user);
          if (!inChain && !sameDept) {
            return;
          }
        }

        results.push(data);
      });
    }

    return results.sort((a, b) => {
      const ta = a.updatedAt?.toMillis?.() || a.submittedAt?.toMillis?.() || 0;
      const tb = b.updatedAt?.toMillis?.() || b.submittedAt?.toMillis?.() || 0;
      return tb - ta;
    });
  } catch (error) {
    console.error('[wrmsService] getRequestsHistoryForApprover error:', error);
    throw error;
  }
};

// ── Manual Add (HR/Admin) ────────────────────────────────────────────────────

export const manualAddRequest = async (requestType, formData, targetEmployee, adminUser) => {
  try {
    const deptGroup = getDeptGroup(targetEmployee.department, targetEmployee.role);
    const chain = getApprovalChain(requestType, deptGroup);
    const requestId = await generateRequestId(requestType);

    // Extract attachmentUrl if it exists
    let attachmentUrl = '';
    let isAttachmentChunked = false;
    const cleanedFormData = { ...formData };

    const CHUNK_THRESHOLD = 800 * 1024; // 800 KB threshold

    if (requestType === 'extra_work' && cleanedFormData.attachmentUrl) {
      if (cleanedFormData.attachmentUrl.length > CHUNK_THRESHOLD) {
        attachmentUrl = cleanedFormData.attachmentUrl;
        cleanedFormData.attachmentUrl = '[chunked]';
        isAttachmentChunked = true;
      }
    }

    const docData = {
      requestId,
      requestType,
      employeeId: targetEmployee.id,
      employeeName: targetEmployee.name,
      teamId: targetEmployee.teamId || targetEmployee.employeeCode || '',
      designation: targetEmployee.designation || '',
      department: targetEmployee.department || '',
      deptGroup,
      approvalChain: chain,
      currentStage: 'approved',
      status: 'approved',
      submittedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      applicationApprovedOn: serverTimestamp(),
      manuallyAddedBy: adminUser.uid || adminUser.id,
      ...cleanedFormData,
      isAttachmentChunked,
    };

    const colRef = collection(db, COLLECTIONS[requestType]);
    const docRef = await addDoc(colRef, docData);

    // Save attachment chunks if needed
    if (isAttachmentChunked && attachmentUrl) {
      await saveAttachmentChunks(requestType, docRef.id, attachmentUrl);
    }

    await addApprovalLog({
      requestId: docRef.id,
      requestType,
      approverId: adminUser.uid || adminUser.id,
      approverRole: adminUser.role,
      approverName: adminUser.employeeName || 'Admin',
      action: 'manual_add',
      stage: 'all',
      remarks: `Manually added by ${adminUser.role}: ${adminUser.employeeName || 'Admin'}`,
    });

    return { success: true, id: docRef.id, requestId };
  } catch (error) {
    console.error('[wrmsService] manualAddRequest error:', error);
    throw error;
  }
};

// ── Internal Helpers ─────────────────────────────────────────────────────────

const addApprovalLog = async (logData) => {
  await addDoc(collection(db, COLLECTIONS.logs), {
    ...logData,
    actedAt: serverTimestamp(),
  });
};

export const getRequestTypeLabel = (type) => {
  const labels = {
    leave: 'Leave Request',
    early_leave: 'Early Leave Request',
    gate_pass: 'Gate Pass',
    extra_work: 'Extra Work',
  };
  return labels[type] || type;
};

const getStageName = (stage) => {
  const names = {
    submitted: 'Employee',
    hr_head: 'HR Head',
    manager: 'Reporting Manager',
    hr: 'HR',
    director: 'Director',
    dept_head: 'Dept Head',
    production_head: 'Production Head',
    approved: 'Approved',
    rejected: 'Rejected',
    cancelled: 'Cancelled',
    on_hold: 'On Hold',
    hold: 'On Hold',
  };
  return names[stage] || stage;
};
